import { useState } from 'react';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

async function loadRecaptcha() {
  if (!RECAPTCHA_SITE_KEY) return null;
  return new Promise(resolve => {
    if (window.grecaptcha) { resolve(window.grecaptcha); return; }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.onload = () => resolve(window.grecaptcha);
    document.head.appendChild(script);
  });
}

export default function ContactForm({ inline = false, userEmail = null, userName = null }) {
  const [name, setName]       = useState(userName || '');
  const [email, setEmail]     = useState(userEmail || '');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const isLoggedIn = !!userEmail;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');

    try {
      let captchaToken = '';
      if (!isLoggedIn && RECAPTCHA_SITE_KEY) {
        const grecaptcha = await loadRecaptcha();
        captchaToken = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
      }

      const endpoint = isLoggedIn ? '/api/contact/app' : '/api/contact';
      const body = isLoggedIn
        ? { message }
        : { email, name, message, captcha_token: captchaToken };

      const headers = { 'Content-Type': 'application/json' };
      if (isLoggedIn) {
        const token = localStorage.getItem('token');
        const accessCode = localStorage.getItem('lc_access_code');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (accessCode) headers['X-Access-Code'] = accessCode;
      }

      const res = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    border: '1.5px solid var(--border)', borderRadius: 8,
    fontSize: 14, background: 'var(--bg)', color: 'var(--ink)',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 13, fontWeight: 500, color: 'var(--ink-2)',
    display: 'block', marginBottom: 6,
  };

  if (sent) return (
    <div style={{ textAlign: 'center', padding: inline ? '24px 0' : 40 }}>
      <p style={{ fontSize: 32, marginBottom: 12 }}>✅</p>
      <p style={{ fontSize: 18, fontWeight: 300, marginBottom: 8 }}>Message received</p>
      <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>
        We'll get back to you at {isLoggedIn ? userEmail : email} within 24 hours.
      </p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {!isLoggedIn && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Your name</label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)}
              placeholder="Jane Smith" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email address *</label>
            <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" required />
          </div>
        </>
      )}
      {isLoggedIn && (
        <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
          Sending as <strong>{userEmail}</strong>
        </p>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Message *</label>
        <textarea style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
          value={message} onChange={e => setMessage(e.target.value)}
          placeholder="How can we help?" required />
      </div>
      {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>}
      <button type="submit" disabled={loading} style={{
        width: '100%', padding: '12px', background: 'var(--ink)',
        color: '#fff', border: 'none', borderRadius: 8,
        fontSize: 15, cursor: loading ? 'wait' : 'pointer',
        opacity: loading ? 0.7 : 1, fontFamily: 'inherit', fontWeight: 600,
      }}>
        {loading ? 'Sending…' : 'Send message →'}
      </button>
      {!isLoggedIn && RECAPTCHA_SITE_KEY && (
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8, textAlign: 'center' }}>
          Protected by reCAPTCHA. Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Privacy</a>
          {' '}&{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Terms</a>
        </p>
      )}
    </form>
  );
}
